"use client";

import type { PricingType } from "./pricingSection.types";
import { pricingVariants } from "../../helpers/pricing/pricingVariants";
import { cn } from "../../lib/utils";
import { AdaptedFee } from "@/helpers/pricing/adapters/adaptToFee";

const formatCurrency = (currency: string, value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 2,
  }).format(value);
};

const amountIsNumber = (amount: number | undefined): amount is number => {
  return typeof amount === "number";
};

function PricingFeeData({ fees, theme }: { fees: AdaptedFee[]; theme: PricingType["theme"] }) {
  return (
    <ul className="flex flex-col gap-2 my-8">
      {fees.map((fee) => {
        const { feeType, amount, maxCurrency, maxAmount, currency, unit, isHighlight, _key } = fee;
        let display = "";

        switch (feeType) {
          case "free":
            display = "Free";
            break;
          case "percentage":
            display = amountIsNumber(amount) ? `${amount}%` : "";
            if (maxAmount) {
              display += ` (max ${maxCurrency || ""} ${maxAmount})`;
            }
            break;
          case "fixed":
            display =
              currency && amountIsNumber(amount) ? `${formatCurrency(currency, amount)}` : "";
            break;
          case "perUnit":
            display =
              currency && amountIsNumber(amount) && unit
                ? `${formatCurrency(currency, amount)}/${unit}`
                : "";
            break;
          case "range":
            display =
              currency && amountIsNumber(amount) ? `${formatCurrency(currency, amount)}` : "";
            if (maxAmount) {
              display += ` - ${formatCurrency(currency || maxCurrency || "", maxAmount)}`;
            }
            break;
          default:
            display = amount?.toString() || "";
        }
        return (
          <li
            className={cn(
              "flex flex-row w-full justify-between rounded-xl py-4 px-2",
              isHighlight && pricingVariants({ highlight: theme }),
              isHighlight && "px-4 ",
            )}
            key={_key}
          >
            <span className="text-[#717182]">{fee.name} </span>
            <span className={cn(pricingVariants({ text: theme }))}>{display}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default PricingFeeData;
